/* ----------------------------------------------------------------------------
 * BatchPublishPanel — Aura publish P1 on-screen cards (owner decision 10-10-2026).
 * Drafts are SHOWN, never read out: each card = title, short excerpt, target
 * keyword + page, and the mode buttons. Every card has Draft and Publish; the
 * whole batch can be confirmed with "Publish all" / "Draft all". Publish is
 * disabled (with the reason) when the site does not allow it. Each published
 * item offers Undo (back to draft). Presentational only: the caller (voice
 * controller or chat wrapper) owns the server calls.
 * -------------------------------------------------------------------------- */

import { cn } from '@/lib/utils';
import {
  cardStateLabel,
  alreadyLiveLabel,
  itemResultLabel,
  pendingDraftIds,
  publishControlState,
  updateAskButtonLabel,
  type BatchMode,
  type BatchPublishView,
} from './batchPublishModel.ts';

export interface BatchPublishPanelProps {
  view: BatchPublishView;
  onConfirm: (mode: BatchMode, draftIds?: number[]) => void;
  onChooseSite: (siteId: number) => void;
  onUndo: (publicationId: number, draftId: number) => void;
  onDismiss: () => void;
  /** 'voice' = the dark Aura popup; 'rail' = the light Copilot rail. */
  variant?: 'voice' | 'rail';
  /** Connector 1.3.0 (owner GO 12-10-2026 15:53 UTC, option A): "Update live post" on an
   *  "Already live" entry -> server read-back; then a tap confirms, or cancel; Undo after. */
  onUpdateLive?: (draftId: number) => void;
  onConfirmUpdateLive?: () => void;
  onCancelUpdateLive?: () => void;
  onUndoUpdateLive?: () => void;
}

export function BatchPublishPanel({ view, onConfirm, onChooseSite, onUndo, onDismiss, variant = 'rail', onUpdateLive, onConfirmUpdateLive, onCancelUpdateLive, onUndoUpdateLive }: BatchPublishPanelProps) {
  const dark = variant === 'voice';
  if (view.phase === 'forbidden') {
    // C6b (owner decision 10-10-2026): roles other than owner/operator get the brief
    // line only — no publish cards, no Draft/Publish/Undo buttons. Server is the authority.
    return (
      <section className={cn('flex items-start justify-between gap-2 rounded-xl border p-3 text-[12px]', dark ? 'border-white/10 bg-white/[0.03] text-[#e8e9ee]' : 'border-border-default bg-canvas/40 text-text-primary')} aria-label="Publishing" role="status">
        <p className="font-medium leading-snug">{view.line}</p>
        <button type="button" onClick={onDismiss} className={cn('rounded-md border px-2 py-0.5 text-[11px]', dark ? 'border-white/15 bg-white/5 text-[#e8e9ee] hover:bg-white/10' : 'border-border-default bg-surface text-text-primary hover:bg-canvas')} aria-label="Close">
          Close
        </button>
      </section>
    );
  }
  const t = {
    box: dark ? 'border-white/10 bg-white/[0.03] text-[#e8e9ee]' : 'border-border-default bg-canvas/40 text-text-primary',
    sub: dark ? 'text-[#9aa0ad]' : 'text-text-tertiary',
    card: dark ? 'border-white/10 bg-[#0f0e16]' : 'border-border-default bg-surface',
    btn: dark ? 'border-white/15 bg-white/5 text-[#e8e9ee] hover:bg-white/10' : 'border-border-default bg-surface text-text-primary hover:bg-canvas',
    primary: dark ? 'border-[#a78bfa]/50 bg-[#a78bfa]/20 text-white hover:bg-[#a78bfa]/30' : 'border-ai/60 bg-ai-wash/60 text-text-primary hover:border-ai hover:bg-ai-wash',
  };
  const busy = view.phase === 'working' || view.phase === 'loading';
  const pending = pendingDraftIds(view);
  const publishCtl = publishControlState(view); // C6: disabled (with the server's reason) for roles that may not publish
  const publishTitle = publishCtl.title;

  return (
    <section className={cn('flex flex-col gap-2 rounded-xl border p-3 text-[12px]', t.box)} aria-label="Push drafts to your site" aria-busy={busy}>
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium leading-snug">{view.line || 'Loading your ready drafts…'}</p>
        <button type="button" onClick={onDismiss} className={cn('rounded-md border px-2 py-0.5 text-[11px]', t.btn)} aria-label="Close">
          Close
        </button>
      </div>

      {view.phase === 'choose_site' && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Choose a site">
          {view.sites.map((s) => (
            <button key={s.id} type="button" onClick={() => onChooseSite(s.id)} className={cn('min-h-[40px] rounded-md border px-3 py-1.5', t.btn)}>
              {s.name}
            </button>
          ))}
        </div>
      )}

      {view.items.length > 0 && (
        <ul className="flex flex-col gap-2">
          {view.items.map((c) => {
            const r = view.results[c.draft_id];
            return (
              <li key={c.draft_id} className={cn('rounded-lg border p-2.5', t.card)}>
                <div className="font-semibold leading-snug">{c.title || 'Untitled draft'}</div>
                {c.excerpt && <p className={cn('mt-0.5 line-clamp-2 leading-snug', t.sub)}>{c.excerpt}</p>}
                <dl className={cn('mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[11px]', t.sub)}>
                  {c.focus_keyword && (<><dt>Keyword</dt><dd className="truncate">{c.focus_keyword}</dd></>)}
                  {c.target_page && (<><dt>Target page</dt><dd className="truncate" title={c.target_page}>{c.target_page}</dd></>)}
                  <dt>Status</dt><dd>{r ? itemResultLabel(r.reverted ? 'drafted' : r.status, r.reverted ? 'ok' : r.reason) + (r.reverted ? ' (undone)' : '') : cardStateLabel(c.state, view.site?.name ?? '')}</dd>
                </dl>
                {!r && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button type="button" disabled={busy} onClick={() => onConfirm('draft', [c.draft_id])} className={cn('min-h-[40px] rounded-md border px-3 py-1.5 font-medium disabled:opacity-50', t.btn)}>
                      Draft
                    </button>
                    <button type="button" disabled={publishCtl.disabled} title={publishTitle} onClick={() => onConfirm('publish', [c.draft_id])} className={cn('min-h-[40px] rounded-md border px-3 py-1.5 font-medium disabled:opacity-50', t.primary)}>
                      Publish
                    </button>
                  </div>
                )}
                {r && r.undo_available && !r.reverted && r.publication_id != null && (
                  <div className="mt-2">
                    <button type="button" disabled={busy} onClick={() => onUndo(r.publication_id as number, c.draft_id)} className={cn('min-h-[40px] rounded-md border px-3 py-1.5 disabled:opacity-50', t.btn)}>
                      Undo (back to draft)
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {(view.alreadyLive ?? []).length > 0 && view.site && (
        <ul className={cn('flex flex-col gap-1 text-[11px]', t.sub)} aria-label="Already live">
          {(view.alreadyLive ?? []).map((a) => (
            <li key={a.draft_id} className="flex flex-wrap items-center gap-2">
              <span><span className="font-medium">{a.title || 'Untitled draft'}</span> — {alreadyLiveLabel(view.site?.name ?? '')}</span>
              {onUpdateLive && (
                <button type="button" disabled={busy || view.liveUpdate?.phase === 'confirm'} onClick={() => onUpdateLive(a.draft_id)} className={cn('min-h-[36px] rounded-md border px-2.5 py-1 font-medium disabled:opacity-50', t.btn)}>
                  Update live post
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {view.updateAsk && view.phase === 'asking' && view.site && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Update the draft already on the site">
          <button type="button" disabled={busy || (view.updateAsk.mode === 'publish' && publishCtl.disabled)} onClick={() => onConfirm(view.updateAsk!.mode, view.updateAsk!.draftIds)} className={cn('min-h-[40px] rounded-md border px-3 py-1.5 font-semibold disabled:opacity-50', t.primary)}>
            {updateAskButtonLabel(view.updateAsk.mode)}
          </button>
        </div>
      )}

      {view.liveUpdate && view.liveUpdate.phase !== 'cancelled' && (
        <div className={cn('rounded-lg border p-2.5', t.card)} aria-label="Update live post" aria-live="polite">
          {view.liveUpdate.readback && <p className="font-medium leading-snug">{view.liveUpdate.readback}</p>}
          {view.liveUpdate.phase === 'confirm' && (
            <div className="mt-2 flex flex-wrap gap-2">
              {onConfirmUpdateLive && (
                <button type="button" onClick={onConfirmUpdateLive} className={cn('min-h-[40px] rounded-md border px-3 py-1.5 font-semibold', t.primary)}>
                  Yes, update live post
                </button>
              )}
              {onCancelUpdateLive && (
                <button type="button" onClick={onCancelUpdateLive} className={cn('min-h-[40px] rounded-md border px-3 py-1.5', t.btn)}>
                  Cancel
                </button>
              )}
            </div>
          )}
          {view.liveUpdate.resultLine && <p className="mt-1 text-[11px]">{view.liveUpdate.resultLine}</p>}
          {view.liveUpdate.phase === 'done' && view.liveUpdate.undoAvailable && onUndoUpdateLive && (
            <div className="mt-2">
              <button type="button" disabled={busy} onClick={onUndoUpdateLive} className={cn('min-h-[40px] rounded-md border px-3 py-1.5 disabled:opacity-50', t.btn)}>
                Undo update (restore previous version)
              </button>
            </div>
          )}
        </div>
      )}

      {pending.length > 1 && (
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={publishCtl.disabled} title={publishTitle} onClick={() => onConfirm('publish')} className={cn('min-h-[40px] rounded-md border px-3 py-1.5 font-semibold disabled:opacity-50', t.primary)}>
            Publish all ({pending.length})
          </button>
          <button type="button" disabled={busy} onClick={() => onConfirm('draft')} className={cn('min-h-[40px] rounded-md border px-3 py-1.5 font-semibold disabled:opacity-50', t.btn)}>
            Draft all ({pending.length})
          </button>
        </div>
      )}

      {!view.publishAvailable && view.items.length > 0 && view.publishMessage && (
        <p className={cn('text-[11px]', t.sub)}>{view.publishMessage}</p>
      )}
      {view.readback && <p className="text-[11px] font-medium" aria-live="polite">{view.readback}</p>}
      {view.resultLine && <p className="text-[11px]" aria-live="polite">{view.resultLine}</p>}
      {view.error && <p className="text-[11px] text-red-400" role="alert">{view.error}</p>}
    </section>
  );
}
